import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  ACTIVE_BOGO_LIST_V2,
  BASE_URL_NODE,
  GET_ALL_BOGO_LIST,
  USING_ACTIVE_BOGO_LIST_API,
} from '../../../Constants/Config';
import { createRequestCache } from '../../../utils/requestCache';

const merchantBogoRequestCache = createRequestCache({
  ttlMs: 30 * 1000,
  maxEntries: 40,
});

const initialState = {
  loading: false,
  bogoList: [],
  error: '',
  requestKey: '',
  activeRequestId: null,
  activeRequestKey: '',
};

export const createBogoRequestKey = (data) => {
  const merchantId = data?.merchant_id;
  if (merchantId === null || merchantId === undefined) return '';

  const normalizedMerchantId = String(merchantId).trim().toUpperCase();
  return normalizedMerchantId ? `merchant:${normalizedMerchantId}` : '';
};

export const fetchBogoLists = createAsyncThunk(
  'bogoList/fetchBogoLists',
  async (data, { rejectWithValue, signal }) => {
    const merchantId = String(data?.merchant_id ?? '').trim().toUpperCase();
    if (!merchantId) {
      return rejectWithValue('Missing merchant identifier');
    }

    const endpoint = USING_ACTIVE_BOGO_LIST_API
      ? ACTIVE_BOGO_LIST_V2
      : GET_ALL_BOGO_LIST;
    const requestKey = JSON.stringify([BASE_URL_NODE, endpoint, merchantId]);

    try {
      return await merchantBogoRequestCache.getOrCreate(
        requestKey,
        async () => {
          const response = await axios.post(
            BASE_URL_NODE + endpoint,
            { merchant_id: merchantId },
            { headers: { 'Content-Type': 'multipart/form-data' } },
          );
          return Array.isArray(response?.data?.bogo_list)
            ? response.data.bogo_list
            : [];
        },
        { forceRefresh: Boolean(data?.forceRefresh), signal },
      );
    } catch (error) {
      return rejectWithValue(error?.message || 'Unable to fetch BOGO deals');
    }
  },
);

const bogoListSlice = createSlice({
  name: 'bogoList',
  initialState,
  extraReducers: (builder) => {
    builder.addCase(fetchBogoLists.pending, (state, action) => {
      const requestId = action.meta?.requestId;
      if (!requestId) return;

      const requestKey = createBogoRequestKey(action.meta?.arg);
      if (state.requestKey !== requestKey) {
        state.bogoList = [];
        state.requestKey = '';
      }
      state.loading = true;
      state.error = '';
      state.activeRequestId = requestId;
      state.activeRequestKey = requestKey;
    });
    builder.addCase(fetchBogoLists.fulfilled, (state, action) => {
      const requestKey = createBogoRequestKey(action.meta?.arg);
      if (
        action.meta?.requestId !== state.activeRequestId ||
        requestKey !== state.activeRequestKey
      ) {
        return;
      }

      state.loading = false;
      state.bogoList = Array.isArray(action.payload) ? action.payload : [];
      state.error = '';
      state.requestKey = requestKey;
      state.activeRequestId = null;
      state.activeRequestKey = '';
    });
    builder.addCase(fetchBogoLists.rejected, (state, action) => {
      const requestKey = createBogoRequestKey(action.meta?.arg);
      if (
        action.meta?.requestId !== state.activeRequestId ||
        requestKey !== state.activeRequestKey
      ) {
        return;
      }

      state.loading = false;
      if (!action.meta?.aborted) {
        state.bogoList = [];
        state.requestKey = '';
        state.error =
          action.payload || action.error?.message || 'Unable to load BOGO deals';
      }
      state.activeRequestId = null;
      state.activeRequestKey = '';
    });
  },
});

export default bogoListSlice.reducer;
